/**
 * Bar component module.
 * @module shared/components/Bar
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {formatter} from '../../utils/utils';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import {withTheme} from 'styled-components/macro';

const adjustLimit = (num, min) => {
  if (num > 0) {
    return min ? num * 0.8 : num * 1.2;
  }
  if (num < 0) {
    return min ? num * 1.2 : num * 0.8;
  }
  return num;
};

// Bar graph max size of tile
const Bar = ({
  height = '100%',
  width = '100%',
  data,
  min,
  max,
  theme,
}) => {
  const options = React.useMemo(() => {
    if (!data || !data.length) {
      return null;
    }
    const raw = data.map(d => d.y);
    return {
      rangeSelector: {
        enabled: false,
      },
      scrollbar: {
        enabled: false,
      },
      legend: {enabled: false},
      credits: {enabled: false},
      title: false,
      chart: {
        type: 'bar',
        //panning: false,
        backgroundColor: 'rgba(255, 255, 255, 0.00)',
        plotBorderWidth: 0,
        plotBorderColor: '#FFF',
        borderRadius: 0,
        //spacingRight:30,
        plotShadow: false,
        zoomType: '',
        pinchType: '',
        height: 200,
      },
      xAxis: {
        type: 'category',
        /*gridLineColor:"#fff",
         gridLineWidth:1,
         tickLength: 0,*/
        labels: {
          style: {
            color: '#ffffff',
          },
          formatter: function labelFormatter() {
            if (this.value.length > 16) {
              return this.value.substring(0, 16) + '...';
            }

            return this.value;
          },
        },
        plotLines: [],
        plotBands: [],
      },
      yAxis: {
        title: {
          text: false,
        },
        labels: {
          enabled: false,
        },
        min: min || adjustLimit(Math.min(...raw), true),
        max: max || adjustLimit(Math.max(...raw), false),
        gridLineWidth: 0,
      },
      series: [{data}],
      plotOptions: {
        bar: {
          borderWidth: theme.neumorphic ? 1 : 0,
          color: theme.neumorphic ? theme.primaryColor : undefined,
          shadow: theme.neumorphic ? {color: '#000000', offsetX: 0, offsetY: 0, opacity: 0.2, width: 6} : false,
          borderColor: theme.neumorphic ? {
            linearGradient: {x1: 0, x2: 1, y1: 1, y2: 1},
            stops: [
              [0, Highcharts.Color('#FFFFFF').setOpacity(0.4).get('rgba')],
              [1, Highcharts.Color('#000000').setOpacity(0.3).get('rgba')],
            ],
          } : undefined,
          dataLabels: {
            enabled: true,
            color: '#fff',
            formatter: function dataFormatter() {
              return formatter('percent', {decimalPlaces: 1})(this.y);
            },
            borderRadius: 5,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderWidth: 1,
            borderColor: '#AAA',
          },
        },
      },
      tooltip: {
        enabled: false,
      },
    };
  }, [data]);

  return <HighchartsReact containerProps={{style: {height, width, ...(theme.neumorphic && {filter: 'drop-shadow(2px 0px 4px rgb(0, 0, 0, 0.5));'})}}}
                          highcharts={Highcharts}
                          options={options}/>;
};

export default withTheme(Bar);

Bar.propTypes = {
  height: PropTypes.string,
  width: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  min: PropTypes.number,
  max: PropTypes.number,
  theme: PropTypes.object,
};
